import type { CarImage } from "@interfaces/gallery";
import { GRID_PATTERN } from "@configs/gallery";

/**
 * Groups images into rows based on a predefined grid pattern
 * @param displayedImages Array of car images to be grouped
 * @returns Array of image groups, where each group represents a row in the grid
 */
export const createImageGroups = (
  displayedImages: CarImage[]
): CarImage[][] => {
  const imageGroups: CarImage[][] = [];
  let start = 0;

  // First 6 images with GRID_PATTERN [1,2,1,2]
  for (const cols of GRID_PATTERN) {
    const end = start + cols;
    const rowImages = displayedImages.slice(start, Math.min(end, 6));
    if (rowImages.length > 0) {
      imageGroups.push(rowImages);
    }
    start = end;
    if (start >= 6) break;
  }

  // Remaining images
  if (displayedImages.length > 6) {
    const remaining = displayedImages.slice(6);
    let current = 0;
    while (current < remaining.length) {
      for (const cols of GRID_PATTERN) {
        const rowImages = remaining.slice(current, current + cols);
        if (rowImages.length > 0) {
          imageGroups.push(rowImages);
        }
        current += cols;
        if (current >= remaining.length) break;
      }
    }
  }

  return imageGroups;
};

interface DesktopGridData {
  imagesPerRow: number[];
  imageChunks: CarImage[][];
}

/**
 * Computes the grid layout data for desktop view
 * @param images Array of car images to be arranged in a grid
 * @returns Object containing the number of images per row and the chunked images
 */
export const computeDesktopGridLayout = (
  images: CarImage[]
): DesktopGridData => {
  const imagesPerRow: number[] = [];
  let remainingImages = images.length;
  let patternIndex = 0;

  // Calculate how many images should be in each row
  while (remainingImages > 0) {
    const cols = GRID_PATTERN[patternIndex % GRID_PATTERN.length];
    const currentChunk = Math.min(cols, remainingImages);
    imagesPerRow.push(currentChunk);
    remainingImages -= currentChunk;
    patternIndex++;
  }

  // Create image chunks based on the calculated row sizes
  const imageChunks = imagesPerRow.map((cols, chunkIndex) => {
    const start = imagesPerRow.slice(0, chunkIndex).reduce((a, b) => a + b, 0);
    return images.slice(start, start + cols);
  });

  return {
    imagesPerRow,
    imageChunks,
  };
};
